import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import {
  CheckOutlined,
  DownOutlined,
  FolderOutlined,
  LockOutlined,
  PlusOutlined,
} from '../../../components/icons'
import type { WorkspaceSummary } from '../../workspaces/types'
import { workspaceTint } from '../workspaceTint'

export interface WorkspaceSelectorProps {
  readonly workspaces: readonly WorkspaceSummary[]
  /** `null` means "keep as draft (unfiled)". */
  readonly selectedWorkspaceId: string | null
  readonly onSelectWorkspace: (id: string) => void
  readonly onSelectDraft: () => void
  readonly onCreateWorkspace: () => void
}

/**
 * "Uploading to <project>" selector (UID-140 S3, `Fictiv Platform.dc.html:
 * 2891-2927` — the UPLOADS LANDING block starts at `:2853`; this comment's
 * previous citation, `:2428-2464`, had drifted into the CHECKOUT
 * shipping/payment block) — a folder chip + label + a dropdown
 * button that opens a menu of assignable projects, "Keep as draft
 * (unfiled)", and "New project". Menu semantics + keyboard support follow
 * the same click-outside/Escape pattern `TopBar.tsx`'s `AccountMenu` already
 * uses, plus roving arrow-key navigation between `role="menuitem"` options
 * (accessibility-wcag: full keyboard operability, not just Tab order).
 *
 * COPY (UID-352): every string that names the CONTAINER says "project", per
 * the product-wide Workspace→Project rename — the comp's `ASSIGN TO PROJECT`
 * (`:2903`), `New project` (`:2920`) and `…a different project` (`:2926`).
 * The CODE still says "workspace" throughout (props, `WorkspaceSummary`,
 * `POST /workspaces`) because only the UI was renamed, not the contract or
 * the data model — the same split `registry.tsx:217-219` records for UID-345
 * ("a route-path-only rename"). Don't "fix" that mismatch.
 *
 * This component does NOT own the create-project modal: it only invokes
 * `onCreateWorkspace`, and the page decides what that means.
 */
export function WorkspaceSelector({
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
  onSelectDraft,
  onCreateWorkspace,
}: WorkspaceSelectorProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const selectedWorkspace = workspaces.find((workspace) => workspace.id === selectedWorkspaceId)
  const label = selectedWorkspace ? selectedWorkspace.name : 'Keep as draft (unfiled)'
  const dotColor = selectedWorkspace
    ? workspaceTint(selectedWorkspace.id)
    : 'var(--color-text-tertiary)'

  function moveFocus(direction: 1 | -1) {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    )
    // QA finding #1 (real WCAG gap, Gate 5 follow-up): a `disabled` button
    // (the locked/no-access workspace rows below) can never take DOM focus,
    // so including it in the roving set left a keyboard user stuck — Arrow
    // Down/Up landed on it as a no-op. Restricting the roving set to
    // focusable (non-disabled) items means the index math naturally skips
    // over a locked row and wraps around to the next real item instead.
    const focusable = items.filter((item) => !item.hasAttribute('disabled'))
    if (focusable.length === 0) return
    const currentIndex = focusable.findIndex((item) => item === document.activeElement)
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + direction + focusable.length) % focusable.length
    focusable[nextIndex]?.focus()
  }

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveFocus(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveFocus(-1)
    }
  }

  return (
    <div className="uploads-workspace-row">
      <span className="uploads-workspace-chip" aria-hidden="true">
        <FolderOutlined />
      </span>
      <span className="uploads-workspace-label">Uploading to</span>
      <div className="uploads-workspace-dropdown" ref={wrapperRef}>
        <button
          ref={triggerRef}
          type="button"
          className="uploads-workspace-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span
            className="uploads-workspace-dot"
            aria-hidden="true"
            style={{ background: dotColor }}
          />
          <span className="uploads-workspace-trigger-label">{label}</span>
          <DownOutlined aria-hidden="true" />
        </button>
        {open && (
          <div
            role="menu"
            aria-label="Assign to project"
            className="uploads-workspace-menu"
            ref={menuRef}
            onKeyDown={handleMenuKeyDown}
          >
            <div className="uploads-workspace-menu-header">ASSIGN TO PROJECT</div>
            {workspaces.map((workspace) => {
              // Forward-looking/defensive (QA #2, code-review): the REAL
              // `GET /api/workspaces` contract never returns a private
              // workspace the caller isn't a member of at all (see
              // `workspaces/types.ts`'s `WorkspaceSummary` doc comment), so
              // this branch is unreachable with today's data — kept anyway
              // to match the design mock's lock-icon state and so a future
              // caller passing a broader workspace list gets this handled
              // for free instead of silently omitting the lock.
              const locked = workspace.visibility === 'private' && workspace.myRole === null
              const selected = workspace.id === selectedWorkspaceId
              return (
                <button
                  key={workspace.id}
                  type="button"
                  role="menuitem"
                  className="uploads-workspace-menu-item"
                  disabled={locked}
                  aria-disabled={locked || undefined}
                  onClick={() => {
                    if (locked) return
                    onSelectWorkspace(workspace.id)
                    setOpen(false)
                    triggerRef.current?.focus()
                  }}
                >
                  <span
                    className="uploads-workspace-dot"
                    aria-hidden="true"
                    style={{ background: workspaceTint(workspace.id) }}
                  />
                  <span className="uploads-workspace-menu-item-label">{workspace.name}</span>
                  {locked && <LockOutlined aria-label="Private — you are not a member" />}
                  {selected && (
                    <CheckOutlined aria-hidden="true" className="uploads-workspace-check" />
                  )}
                </button>
              )
            })}
            <div className="uploads-workspace-menu-divider" aria-hidden="true" />
            <button
              type="button"
              role="menuitem"
              className="uploads-workspace-menu-item"
              onClick={() => {
                onSelectDraft()
                setOpen(false)
                triggerRef.current?.focus()
              }}
            >
              <FolderOutlined aria-hidden="true" />
              <span className="uploads-workspace-menu-item-label">Keep as draft (unfiled)</span>
              {selectedWorkspaceId === null && (
                <CheckOutlined aria-hidden="true" className="uploads-workspace-check" />
              )}
            </button>
            <button
              type="button"
              role="menuitem"
              className="uploads-workspace-menu-item uploads-workspace-menu-item-new"
              onClick={() => {
                setOpen(false)
                // UID-353: move focus to the trigger, which renders
                // unconditionally above and so is always available. This click
                // unmounts the menu — including this very button — leaving
                // `document.activeElement` as <body>. `Modal` snapshots
                // `activeElement` when it opens so it can restore focus on
                // close (see its doc comment), so without this a keyboard user
                // closing the create-project modal is dumped at the top of the
                // document. `Modal`'s own <h1> fallback does NOT cover this:
                // it only triggers when the snapshot is DETACHED, and
                // `document.body.contains(document.body)` is true.
                // The two sibling items above already do the same thing.
                triggerRef.current?.focus()
                onCreateWorkspace()
              }}
            >
              <PlusOutlined aria-hidden="true" />
              <span className="uploads-workspace-menu-item-label">New project</span>
            </button>
          </div>
        )}
      </div>
      <span className="uploads-workspace-hint">
        Preselected — change if you need a different project
      </span>
    </div>
  )
}
